import { useSuspenseHttpQuery } from '@/resources/http/useHttpQuery';
import { isAuthorized, RoleTest, usePermissions } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import * as React from 'react';
import { isAuthorizedForModule, ModulePermissionTest } from './isAuthorizedForModule';
import { useModulePermissions } from './useModulePermissions';

interface Props {
    /**
     * A list of all the required permissions. The children will only be
     * rendered if all the required permissions are met.
     */
    requiredPermissions: RoleTest;
    /**
     * Same as above, but for the advanced access management defined in spicedb
     * Only takes effect if is_advanced_role_management_enabled feature flag is set.
     * If not supplied, `requiredPermissions` will be respected regardless of the feature flag
     */
    requiredModulePermissions?: ModulePermissionTest;
    /**
     * Use this as fallback content in case not all the required permissions
     * are met.
     */
    fallback?: JSX.Element;
}

/**
 * This component will only render its children when the `requiredPermissions` are met.
 */
export const Protected = React.forwardRef<unknown, React.PropsWithChildren<Props>>(
    ({ requiredPermissions, requiredModulePermissions, fallback, children }, ref) => {
        const {
            data: { is_advanced_role_management_enabled },
        } = useSuspenseHttpQuery('GET /organization-settings/feature-flags', {}, { staleTime: Infinity });

        const modulePermissions = useModulePermissions();

        const { permissions } = usePermissions();

        const success =
            isPresent(requiredModulePermissions) && is_advanced_role_management_enabled
                ? isAuthorizedForModule(modulePermissions, requiredModulePermissions)
                : isAuthorized(permissions, requiredPermissions);

        if (!success) {
            return fallback ?? null;
        }

        const child = React.Children.only(children);

        if (React.isValidElement<{ ref: unknown }>(child)) {
            // TODO - fix useRef
            return React.cloneElement(child, { ref });
        }

        return <>{child}</>;
    },
);
